import React, { useEffect, useState } from 'react';
import * as Styles from './styles';
import { Alert, Button, Input, Modal, Popconfirm, Space, Tag, Typography } from 'antd';
import { LockOutlined, UnlockOutlined } from '@ant-design/icons';
import { useFormik } from 'formik';
import { useAppDispatch, useAppSelector } from 'shared/hooks';
import { closeModal } from 'services/store/reducers/modalReducer';
import { ESnackbarStyle, ModalState, TableSchool } from 'shared/types';
import { validation } from 'services/validation';
import { getFieldError } from 'utils/error-utils';
import { openNotification } from 'utils/notification-utils';
import {
  clearSchoolPassword,
  getSchoolRequiresPassword,
  setSchoolPassword
} from 'services/firebase/schoolPasswordService';

type Props = {
  school: TableSchool;
  onChanged?: () => void;
};

/**
 * Super-admin control for a school's VR shared password.
 *
 * There is no "current password" field: the secret is stored as a salted scrypt hash
 * in a collection no client can read, so a password can only be replaced, never shown.
 */
const SchoolPasswordModal: React.FC<Props> = ({ school, onChanged }): JSX.Element => {
  const dispatch = useAppDispatch();
  const { isModalOpened } = useAppSelector((state): ModalState => state.modal);
  const [requiresPassword, setRequiresPassword] = useState<boolean>(!!school.requiresPassword);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isClearing, setIsClearing] = useState<boolean>(false);

  const isBusy = isSubmitting || isClearing;

  // The row that opened this modal comes from the Postgres mirror, which never carries
  // the flag. Re-read it from Firestore so the status and the Clear button are accurate.
  useEffect((): (() => void) => {
    let isActive = true;
    getSchoolRequiresPassword(school.id)
      .then((flag): void => {
        if (isActive) setRequiresPassword(flag);
      })
      .catch((): void => {
        // Keep whatever the row supplied; a failed refresh should not block setting one.
      });
    return (): void => {
      isActive = false;
    };
  }, [school.id]);

  const handleCancel = (): void => {
    if (isBusy) return;
    dispatch(closeModal());
  };

  const formik = useFormik({
    initialValues: { password: '', confirmPassword: '' },
    validationSchema: validation.SET_SCHOOL_PASSWORD,
    onSubmit: async (values): Promise<void> => {
      setIsSubmitting(true);
      try {
        await setSchoolPassword(school.id, values.password);
        openNotification(
          ESnackbarStyle.SUCCESS,
          requiresPassword
            ? `Password updated for ${school.name}.`
            : `Password set for ${school.name}. Headsets will be asked for it on next login.`
        );
        onChanged?.();
        dispatch(closeModal());
      } catch (error: any) {
        openNotification(ESnackbarStyle.ERROR, error.message);
      } finally {
        setIsSubmitting(false);
      }
    }
  });

  const handleClear = async (): Promise<void> => {
    setIsClearing(true);
    try {
      await clearSchoolPassword(school.id);
      openNotification(ESnackbarStyle.SUCCESS, `Password cleared for ${school.name}.`);
      onChanged?.();
      dispatch(closeModal());
    } catch (error: any) {
      openNotification(ESnackbarStyle.ERROR, error.message);
    } finally {
      setIsClearing(false);
    }
  };

  return (
    <Modal
      open={isModalOpened}
      title={`VR password — ${school.name}`}
      onCancel={handleCancel}
      maskClosable={!isBusy}
      footer={[
        <Button key='cancel' onClick={handleCancel} disabled={isBusy}>
          Cancel
        </Button>,
        <Button
          key='save'
          type='primary'
          loading={isSubmitting}
          disabled={isClearing}
          onClick={(): void => formik.handleSubmit()}
        >
          {requiresPassword ? 'Replace password' : 'Set password'}
        </Button>
      ]}
    >
      <Styles.StatusContainer>
        <Space>
          <Typography.Text type='secondary'>Status:</Typography.Text>
          {requiresPassword ? (
            <Tag icon={<LockOutlined />} color='green'>
              Password required
            </Tag>
          ) : (
            <Tag icon={<UnlockOutlined />}>No password — open</Tag>
          )}
        </Space>
      </Styles.StatusContainer>

      <Typography.Paragraph type='secondary'>
        Students enter this once per headset. It is shared by everyone at the school and is
        separate from dashboard sign-in passwords. It is case-sensitive, and cannot be read
        back later — only replaced or cleared.
      </Typography.Paragraph>

      <form onSubmit={formik.handleSubmit}>
        <Styles.InputContainer>
          <Input.Password
            name='password'
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder={requiresPassword ? 'New password' : 'Password'}
            disabled={isBusy}
            autoComplete='new-password'
          />
          {getFieldError(formik, 'password')}
        </Styles.InputContainer>
        <Styles.InputContainer>
          <Input.Password
            name='confirmPassword'
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder='Confirm password'
            disabled={isBusy}
            autoComplete='new-password'
          />
          {getFieldError(formik, 'confirmPassword')}
        </Styles.InputContainer>
      </form>

      {requiresPassword && (
        <Alert
          type='info'
          showIcon
          message='Headsets already signed in keep working'
          description='Replacing or clearing the password does not sign out headsets that already entered the old one. They are asked again the next time their session is reset.'
        />
      )}

      {requiresPassword && (
        <Styles.ClearContainer>
          <Popconfirm
            title='Clear the school password?'
            description={`Anyone will be able to write ${school.name}'s data from VR again.`}
            okText='Clear password'
            okButtonProps={{ danger: true }}
            cancelText='Cancel'
            onConfirm={handleClear}
            disabled={isBusy}
          >
            <Button danger loading={isClearing} disabled={isSubmitting}>
              Clear password
            </Button>
          </Popconfirm>
          <Typography.Paragraph type='secondary' style={{ marginTop: 8, marginBottom: 0 }}>
            Returns the school to open access — no password prompt in VR.
          </Typography.Paragraph>
        </Styles.ClearContainer>
      )}
    </Modal>
  );
};

export default SchoolPasswordModal;
